/**
 * A starter/reference component
 * copy & paste and replace with relevant content
 * or use simply as a reference
 */
import React, { useContext, useEffect, useState } from 'react';
import PropTypes from 'prop-types';

import { Box } from '@zillow/constellation-10';

import { ModularListingContext } from '../../../containers/CreateListingPage/context';
import { WizardContext } from '../../Wizard/context';

import { DISPLAY_SERVER_ERROR_AT } from '../../../constants/WizardConstants';

import WizardAlert from '../../WizardAlert';
import WizardTipCardContainer from '../../WizardTipCardContainer';
import PageWizard from '../../Wizard/PageWizard';

import {
  WizardForm,
  WizardFormSectionContainer,
  WizardHeading,
  WizardTipCardLeftSpacing,
} from '../../../styles/wizard';

import WizardTipContent from './components/WizardTipContent';
import GenderPreferenceFormField from './components/GenderPreferenceFormField';

const propTypes = {
  onGoToPrevStep: PropTypes.func.isRequired,
  onGoToNextStep: PropTypes.func.isRequired,
  wizardTitle: PropTypes.node.isRequired,
  progressStepper: PropTypes.node.isRequired,
  totalSteps: PropTypes.number.isRequired,
  currentStepIndex: PropTypes.number.isRequired,
  showDesktopViewBreakpoint: PropTypes.oneOf(['md' | 'xl' | 'xxl']).isRequired,
};

const defaultProps = {};

const RenterPreferences = ({
  onGoToNextStep,
  onGoToPrevStep,
  onExitWizard,
  wizardTitle,
  progressStepper,
  totalSteps,
  currentStepIndex,
  showDesktopViewBreakpoint,
  ...rest
}) => {
  const {
    wizardState: { isFetchingData },
  } = useContext(WizardContext);

  const {
    modularListingState: { roomForRentFlow, serverErrorObj },
  } = useContext(ModularListingContext);

  const [clickedNext, setClickedNext] = useState(false);
  const [clickedBackNext, setClickedBack] = useState(false);

  useEffect(() => {
    const { fieldName } = serverErrorObj;

    if (!fieldName && !isFetchingData) {
      checkNavToStep();
    }
  }, [isFetchingData]);

  const checkNavToStep = () => {
    if (clickedNext) {
      onNextClick();
    }

    if (clickedBackNext) {
      onBackClick();
    }
  };

  const onNextClick = () => {
    if (isFetchingData) {
      setClickedNext(true);
      return;
    }

    onGoToNextStep();
  };

  const onBackClick = () => {
    if (isFetchingData) {
      setClickedBack(true);
      return;
    }

    onGoToPrevStep();
  };

  return (
    <PageWizard
      title=""
      wizardTitle={wizardTitle}
      progressStepper={progressStepper}
      totalSteps={totalSteps}
      currentStepIndex={currentStepIndex}
      onNextClick={onNextClick}
      onBackClick={onBackClick}
      onExitTriggered={onExitWizard}
      showDesktopViewBreakpoint={showDesktopViewBreakpoint}
      {...rest}
    >
      <div data-testid="RenterPreferences">
        <WizardAlert serverErrorObj={serverErrorObj} displayAt={DISPLAY_SERVER_ERROR_AT.PAGE} />

        <Box
          css={{
            display: 'grid',
            gridTemplateColumns: {
              base: 'repeat(6, 1fr)',
              lg: 'repeat(12, 1fr)',
            },
            gap: '400',
          }}
        >
          <Box css={{ gridColumnEnd: { base: 'span 6', lg: 'span 12', xl: 'span 7' } }}>
            {/* cast as div to prevent cannot appear as a descendant of form error */}
            <WizardForm asChild={true} onSubmit={(e) => e.preventDefault()}>
              <div>
                <WizardFormSectionContainer data-testid="DescribeRoommates">
                  <WizardHeading level={1} textStyle="heading-sm">
                    Do you have any gender preferences?
                  </WizardHeading>
                  <GenderPreferenceFormField />
                </WizardFormSectionContainer>
              </div>
            </WizardForm>
          </Box>

          {/* tip component */}
          <Box css={{ gridColumnEnd: { base: 'span 6', lg: 'span 12', xxl: 'span 5' } }}>
            <WizardTipCardLeftSpacing>
              <WizardTipCardContainer header="Gender preference" showDesktopViewBreakpoint={showDesktopViewBreakpoint}>
                <WizardTipContent />
              </WizardTipCardContainer>
            </WizardTipCardLeftSpacing>
          </Box>
        </Box>
      </div>
    </PageWizard>
  );
};

RenterPreferences.propTypes = propTypes;
RenterPreferences.defaultProps = defaultProps;

export default RenterPreferences;
